<template>
  <div ref="wrap" class="relative">
    <!-- Hidden input so forms can submit the value -->
    <input type="hidden" :name="name" :value="modelKey ?? ''" />

    <!-- Trigger (acts like input) -->
    <div
      ref="btnRef"
      :role="searchable ? undefined : 'button'"
      tabindex="0"
      class="p-2 w-full border-b border-rhino/50 text-mouse shadow-sm-light flex items-center justify-between min-h-[2.5rem]"
      :aria-expanded="!searchable ? (open ? 'true' : 'false') : undefined"
      :aria-controls="!searchable && open ? panelId : undefined"
      :aria-haspopup="!searchable ? 'listbox' : undefined"
      :data-open="open"
      :data-testid="testId ? `${testId}-trigger` : undefined"
      @click="onTriggerClick"
      @keydown.tab="open = false"
      @keydown.down.prevent="openAndFocus('down')"
      @keydown.up.prevent="openAndFocus('up')"
      @keydown.enter.prevent="open ? commitHighlight() : openAndFocus()"
      @keydown.space.prevent="open ? (open = false) : openAndFocus()"
      @keydown.esc.prevent="open = false"
      @keydown="onTypeAhead"
    >
      <template v-if="searchable">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          :class="[
            'w-full bg-transparent outline-none text-left truncate text-mouse',
            selectedLabel ? 'placeholder-mouse' : 'placeholder-rhino',
          ]"
          :placeholder="selectedLabel || searchPlaceholder || placeholder"
          :aria-label="searchPlaceholder || placeholder || 'Search'"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="open ? 'true' : 'false'"
          :aria-controls="open ? panelId : undefined"
          :aria-activedescendant="activeId"
          @focus="openAndFocus()"
          @click.stop
          @keydown.down.prevent="openAndFocus('down')"
          @keydown.up.prevent="openAndFocus('up')"
          @keydown.home.prevent="highlight(0)"
          @keydown.end.prevent="highlight(visibleItems.length - 1)"
          @keydown.enter.prevent="open ? commitHighlight() : openAndFocus()"
          @keydown.esc.prevent="closeAndFocusButton()"
        />
      </template>
      <template v-else>
        <span
          class="truncate"
          :class="selectedLabel ? 'text-mouse' : 'text-rhino'"
        >
          {{ selectedLabel || placeholder }}
        </span>
      </template>

      <!-- chevron -->
      <fa-icon
        :icon="faChevronDown"
        class="size-4 shrink-0 text-rhino ms-3 transition-transform duration-150"
        :class="open ? 'rotate-180' : 'rotate-0'"
      />
    </div>

    <!-- Panel -->
    <div
      v-if="open"
      :id="panelId"
      ref="panelRef"
      class="absolute z-10 w-full max-h-96 overflow-auto rounded-b-md bg-depth-0 shadow-lg p-3"
      role="listbox"
      :data-testid="testId ? `${testId}-listbox` : undefined"
      :aria-activedescendant="!searchable ? activeId : undefined"
      tabindex="0"
      @keydown.down.prevent="moveHighlight('down')"
      @keydown.up.prevent="moveHighlight('up')"
      @keydown.home.prevent="highlight(0)"
      @keydown.end.prevent="highlight(visibleItems.length - 1)"
      @keydown.enter.prevent="commitHighlight()"
      @keydown.esc.prevent="closeAndFocusButton()"
    >
      <template v-if="$slots.panel">
        <slot
          name="panel"
          :visible-items="visibleItems"
          :highlighted="highlighted"
          :id-for="idFor"
          :is-selected="isSelected"
          :select="select"
          :highlight="highlight"
          :move-highlight="moveHighlight"
          :commit-highlight="commitHighlight"
          :close="closeAndFocusButton"
          :active-id="activeId"
          :panel-id="panelId"
        ></slot>
      </template>
      <template v-else>
        <div v-if="!visibleItems.length" class="text-rhino text-sm">
          {{ $t('general/no_results') || 'No results' }}
        </div>
        <template v-else>
          <button
            v-for="(opt, i) in visibleItems"
            :id="idFor(i)"
            :key="getKey(opt).toString()"
            type="button"
            role="option"
            :data-testid="testId ? `${testId}-option` : undefined"
            :aria-selected="isSelected(opt)"
            class="w-full flex justify-between text-left rounded-md p-2 transition hover:outline hover:outline-brand focus-visible:outline-brand cursor-pointer"
            :class="[
              isSelected(opt)
                ? 'bg-brand text-onyx'
                : 'bg-depth-2/60 text-mouse',
              highlighted === i ? 'outline outline-brand' : '',
            ]"
            @click="select(opt)"
            @mouseenter="highlight(i)"
            @focusin="highlight(i)"
          >
            <span class="truncate">{{ getLabel(opt) }}</span>
            <span
              v-if="getSecondary && getSecondary(opt)"
              class="text-sm ms-2 truncate"
              :class="isSelected(opt) ? 'text-onyx/75' : 'text-rhino'"
            >
              {{ getSecondary(opt) }}
            </span>
          </button>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

// Generic model
const model = defineModel<unknown | undefined>();

/**
 * GpSelect access patterns
 * - Props + Accessors: Pass plain items and provide accessors for key/label/secondary/value.
 *   Good for quick usage and simple lists. Filtering uses getLabel by default.
 * - Slot (panel): For fully custom rendering, use the named slot and the exposed helpers
 *   (visible-items, visible-indexed, id-for, is-selected, select, highlight, etc.).
 *   This keeps logic here while letting callers control markup.
 */
const props = withDefaults(
  defineProps<{
    items: unknown[];
    placeholder?: string;
    name?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
    // Accessors
    getKey?: (item: unknown) => string | number;
    getLabel?: (item: unknown) => string;
    getSecondary?: (item: unknown) => string | undefined;
    getValue?: (item: unknown) => unknown;
    // Prefix for data-testid on trigger, listbox and options (e2e hooks)
    testId?: string;
  }>(),
  {
    items: () => [],
    placeholder: '',
    name: 'select',
    searchable: true,
    searchPlaceholder: '',
    getKey: undefined,
    getLabel: undefined,
    getSecondary: undefined,
    getValue: undefined,
    testId: undefined,
  },
);

const getKey = (item: unknown): string | number => {
  if (!(item && typeof item === 'object')) return '';
  if (props.getKey) return props.getKey(item);
  // @ts-expect-error index access for common keys
  return (item.id ?? item.value ?? item.key ?? '').toString();
};
const getLabel = (item: unknown): string => {
  if (!(item && typeof item === 'object')) return '';
  if (props.getLabel) return props.getLabel(item);
  // @ts-expect-error index access for common keys
  return (item.name ?? item.label ?? '').toString();
};
const getSecondary = (item: unknown): string | undefined => {
  if (item == null) return undefined;
  return props.getSecondary
    ? ((props.getSecondary(item) as string | undefined) ?? undefined)
    : undefined;
};

const wrap = ref<HTMLElement | undefined>(undefined);
const btnRef = ref<HTMLElement | undefined>(undefined);
const panelRef = ref<HTMLDivElement | undefined>(undefined);
const searchInput = ref<HTMLInputElement | undefined>(undefined);

const open = ref(false);
const highlighted = ref<number>(-1);
const searchQuery = ref('');

const items = computed(() => props.items ?? []);

const selectedItem = computed(() => {
  if (model.value == null) return undefined;
  if (props.getValue) {
    return (
      items.value.find((item) => props.getValue!(item) === model.value) ??
      undefined
    );
  }
  return (
    items.value.find((item) => getKey(item) === getKey(model.value)) ??
    undefined
  );
});

const selectedKey = computed(() =>
  selectedItem.value ? getKey(selectedItem.value) : '',
);

const selectedLabel = computed(() =>
  selectedItem.value ? getLabel(selectedItem.value) : '',
);

const modelKey = computed(() => selectedKey.value);

const visibleItems = computed<unknown[]>(() => {
  const list = (props.items ?? []) as unknown[];
  const q = props.searchable ? searchQuery.value.trim().toLowerCase() : '';
  if (!q) return list;
  return list.filter((o) => getLabel(o).toLowerCase().includes(q));
});

const indexOfSelected = computed(() => {
  if (!selectedItem.value) return -1;
  const key = selectedKey.value;
  return visibleItems.value.findIndex((item) => getKey(item) === key);
});

const primeHighlight = () => {
  const indexFromSelection = indexOfSelected.value;
  let newHighlighted = -1;
  if (indexFromSelection >= 0) {
    newHighlighted = indexFromSelection;
  } else if (visibleItems.value.length) {
    newHighlighted = 0;
  } else {
    newHighlighted = -1;
  }
  highlighted.value = newHighlighted;
};

const panelId = `gpselect-panel-${Math.random().toString(36).slice(2)}`;
const idFor = (i: number) => `${panelId}-opt-${i}`;

const activeId = computed(() => {
  if (highlighted.value < 0) return undefined;
  return idFor(highlighted.value);
});

const isSelected = (opt: unknown) => {
  if (opt == null) return false;
  const key = selectedKey.value;
  if (key === '') return false;
  return key === getKey(opt);
};

const moveHighlight = (dir: 'up' | 'down') => {
  const n = visibleItems.value.length;
  if (!n) return;
  if (highlighted.value < 0) {
    highlighted.value = 0;
    return;
  }
  highlighted.value = (highlighted.value + (dir === 'down' ? 1 : -1) + n) % n;
};

const openAndFocus = (primeDir?: 'up' | 'down') => {
  if (!open.value) {
    open.value = true;
    // start from current selection if present, else top
    primeHighlight();
    nextTick(() => {
      if (props.searchable) searchInput.value?.focus();
      else panelRef.value?.focus();
    });
  }
  if (primeDir) moveHighlight(primeDir);
};

const toggle = () => {
  open.value = !open.value;
  nextTick(() => {
    if (open.value) {
      if (props.searchable) searchInput.value?.focus();
      else panelRef.value?.focus();
      // prime highlight to selection or top
      primeHighlight();
    }
  });
};

const onTriggerClick = (e: MouseEvent) => {
  if (props.searchable) {
    if (!open.value) {
      openAndFocus();
    } else {
      const target = e.target as Node;
      if (searchInput.value && searchInput.value.contains(target)) return;
      open.value = false;
    }
  } else {
    toggle();
  }
};

const closeAndFocusButton = () => {
  open.value = false;
  nextTick(() => btnRef.value?.focus());
};

const select = (opt: unknown) => {
  const nextVal = props.getValue ? props.getValue(opt) : opt;
  const optionKey = getKey(opt);
  if (selectedKey.value !== optionKey) {
    model.value = nextVal as unknown;
  }
  searchQuery.value = '';
  closeAndFocusButton();
};

const highlight = (i: number) => {
  highlighted.value = i;
};

const commitHighlight = () => {
  const opt = visibleItems.value[highlighted.value];
  if (opt) select(opt);
};

/** Outside-click: attach only while open */
let offPointerDown: ((e: PointerEvent) => void) | undefined;

watch(open, (val) => {
  if (val) {
    offPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (wrap.value && !wrap.value.contains(target)) {
        open.value = false;
      }
    };
    window.addEventListener('pointerdown', offPointerDown, { passive: true });
  } else if (offPointerDown) {
    window.removeEventListener('pointerdown', offPointerDown as EventListener);
    offPointerDown = undefined;
  }
});

watch(searchQuery, () => {
  // reset highlight to first visible item when query changes
  highlighted.value = visibleItems.value.length ? 0 : -1;
});

const onTypeAhead = (e: KeyboardEvent) => {
  if (open.value) return; // when closed only
  if (e.key.length === 1 && /[\p{L}\p{N} ]/u.test(e.key)) {
    e.preventDefault();
    open.value = true;
    searchQuery.value = e.key;
    nextTick(() => searchInput.value?.focus());
  }
};

onUnmounted(() => {
  if (offPointerDown) {
    window.removeEventListener('pointerdown', offPointerDown as EventListener);
    offPointerDown = undefined;
  }
});
</script>
